import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Stories, Notifications } from '../../store/slices';
import { createGiftCard, disableGiftCard } from '../../api';
import { EmptyState, Panel, StatusChip } from '../ui';
import { giftCardStatus } from '../../lib/storyOptions';
import { formatAmount } from '../../lib/formatDate';
import { useStyles } from './styles';

const DEFAULT_PAGING_STATE = {
    page: 1,
    total: 0,
};

/** Gift cards issued to the author of this story: create one, disable one, page through the list (10 per page). */
export default function GiftCadsForm(props: any) {
    const { data, status } = props.data;
    const dispatch = useDispatch();
    const classes = useStyles();
    const [inputValue, setInputValue] = useState<string>('');
    const [pending, setPending] = useState(false);
    const [paging, setPaging] = useState(DEFAULT_PAGING_STATE);

    const loadStoryGiftCards = (queryParams?: { page?: number }) => {
        dispatch<any>(Stories.getStoryGiftCarts({
            id: data.storyId || 0,
            queryParams: { page: queryParams?.page || DEFAULT_PAGING_STATE.page },
        }));
    };

    const createGiftCart = async () => {
        if (pending) return;
        setPending(true);
        try {
            await createGiftCard({
                data: { gfStoryId: data.storyId, gfAuthorId: data.authorId, amount: +inputValue },
                onSuccess: async () => {
                    await loadStoryGiftCards();
                    dispatch(Notifications.setNotifications({ text: 'Gift card created', type: 'success' }));
                },
            });
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: 'Something went wrong, please try again later', type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    const disableGiftCart = async (id) => {
        if (pending) return;
        setPending(true);
        try {
            await disableGiftCard({
                id,
                onSuccess: async () => {
                    await loadStoryGiftCards();
                    dispatch(Notifications.setNotifications({ text: 'Gift card disabled', type: 'success' }));
                },
            });
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: 'Something went wrong, please try again later', type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPaging({ ...paging, page: newPage + 1 });
        loadStoryGiftCards({ page: newPage + 1 });
    };

    useEffect(() => {
        loadStoryGiftCards({ page: DEFAULT_PAGING_STATE.page });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cards: any[] = data.giftCards || [];
    const busy = pending || status === 'loading';
    const amountValid = Number(inputValue) > 0;

    return (
        <Panel title="Gift cards" badges={cards.length > 0 ? <StatusChip size="sm" tone="neutral">{data.giftCardsTotal ?? cards.length}</StatusChip> : undefined} padding="none">
            <div className={classes.createRow}>
                <TextField
                    className={classes.amount}
                    label="Amount"
                    type="number"
                    size="small"
                    value={inputValue}
                    inputProps={{ inputMode: 'numeric', min: 1 }}
                    onChange={(event) => setInputValue(event.target.value)}
                    disabled={busy}
                    helperText={inputValue !== '' && !amountValid ? 'Enter an amount above 0' : ' '}
                    error={inputValue !== '' && !amountValid}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={createGiftCart} disabled={busy || !amountValid}>
                    Create gift card
                </Button>
            </div>
            <div className={busy ? classes.busy : undefined} aria-busy={busy}>
                {cards.length > 0 ? (
                    <>
                        <Table aria-label="Gift cards">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Gift card</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cards.map((row) => {
                                    const cardStatus = giftCardStatus(row.giftcardStatusId);
                                    return (
                                        <TableRow key={row.giftcardId}>
                                            <TableCell className={classes.mono}>{row.giftcardId}</TableCell>
                                            <TableCell className={classes.numeric}>{formatAmount(row.amount)}</TableCell>
                                            <TableCell><StatusChip size="sm" tone={cardStatus.tone}>{cardStatus.label}</StatusChip></TableCell>
                                            <TableCell align="right">
                                                <Button variant="outlined" color="error" size="small" disabled={row.giftcardStatusId === 3 || busy} onClick={() => disableGiftCart(row.giftcardId)}>
                                                    Disable
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[10]}
                            component="div"
                            count={data.giftCardsTotal || paging.total}
                            rowsPerPage={10}
                            page={paging.page - 1}
                            onPageChange={handleChangePage}
                        />
                    </>
                ) : (
                    <EmptyState title="No gift cards yet" hint="Create one above; it is issued to the author of this story." />
                )}
            </div>
        </Panel>
    );
}
